// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ConfirmDeleteActions from '../components/ConfirmDeleteActions';
import { RetryErrorState } from '../components/RetryErrorState';
import { CenteredLoader } from '../components/CenteredLoader';
import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { rolesAPI, menusAPI, type UniformCheckEngine } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { EditRoleModal, uniformCheckEngineOf } from '../components/EditRoleModal';
import { FeatureMatrixTable, type FeatureMatrixGroup } from '../components/FeatureMatrixTable';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { pageRightNotes, type PageRightEntry } from './pageRightNotes';

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  category?: string;
  geo_based_attendance?: boolean;
  multi_store_deployment?: boolean;
  aws_face_verification?: boolean;
  uniform_check_engine?: UniformCheckEngine;
  data_scope?: 'all_project' | 'hierarchy';
  user_count?: number;
  created_at: string;
  updated_at: string;
}

interface MenuItem {
  id: string;
  name: string;
  code: string;
  path?: string | null;
  parent_id: string | null;
  display_order: number;
  is_active: boolean;
}

export interface WebAccessActions {
  onSave: () => void;
  saving: boolean;
  hasChanges: boolean;
}

const WebAccessPage = forwardRef<WebAccessActions, { onChangeNotify?: () => void; refreshKey?: number }>(function WebAccessPage(props, ref) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [roles, setRoles] = useState<Role[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  // What each page's tick also allows (the server's map). Empty until it loads, or if it
  // cannot: the grid then simply shows no "also:" lines.
  const [pageRights, setPageRights] = useState<PageRightEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Local state for permission changes (role id -> permissions array)
  const [permissionChanges, setPermissionChanges] = useState<Record<string, string[]>>({});

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deletingRole, setDeletingRole] = useState(false);
  const [editingRole, setEditingRole] = useState<{ id: string; name: string; description: string; geoBased: boolean; awsVerification: boolean; uniformCheckEngine: UniformCheckEngine; multiStore: boolean; allProject: boolean } | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Fetch roles and menus
  const fetchData = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      const [rolesResponse, menusResponse, mappingsResponse] = await Promise.all([
        rolesAPI.getByProject(selectedProjectId),
        menusAPI.getAll(),
        menusAPI.getAllMappings(),
      ]);
      
      if (rolesResponse.data.success) {
        setRoles(rolesResponse.data.data);
      }
      
      // Initialize permission changes from role_menu_mapping table
      const changes: Record<string, string[]> = {};
      if (rolesResponse.data.success) {
        // Initialize empty arrays for all roles
        rolesResponse.data.data.forEach((role: Role) => {
          changes[role.id] = [];
        });
      }
      
      // Populate from role_menu_mapping
      if (mappingsResponse.data.success && mappingsResponse.data.data) {
        mappingsResponse.data.data.forEach((mapping: { role_id: string; menu_id: string }) => {
          if (!changes[mapping.role_id]) {
            changes[mapping.role_id] = [];
          }
          changes[mapping.role_id].push(mapping.menu_id);
        });
      }
      setPermissionChanges(changes);
      
      if (menusResponse.data.success) {
        // Sort menus: parent menus first by display_order, then children
        const menus = menusResponse.data.data
          .filter((m: MenuItem) => m.is_active)
          .filter((m: any) => {
            const name = String(m?.name || '').toLowerCase().trim();
            const code = String(m?.code || '').toLowerCase().trim();
            const path = String(m?.path || '').toLowerCase().trim();
            if (path === '/settings/menus') return false;
            if (code === 'menu_management' || code === 'menus') return false;
            if (name === 'menu management') return false;
            // Super-admin-only menus — not assignable to roles
            if (code === 'project_management' || code === 'admin_users') return false;
            return true;
          });
        setMenuItems(menus);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [fetchData, selectedProjectId, props.refreshKey]);

  // Loaded on its own: a failure here must not stop anyone managing access.
  useEffect(() => {
    let cancelled = false;
    menusAPI
      .getPageRights()
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.data)) setPageRights(res.data.data as PageRightEntry[]);
      })
      .catch(() => {
        /* no "also:" lines */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Check if a role has a specific permission
  const hasPermission = (roleId: string, menuId: string): boolean => {
    return permissionChanges[roleId]?.includes(menuId) || false;
  };

  // Toggle permission for a role
  const togglePermission = (roleId: string, menuId: string) => {
    setPermissionChanges((prev) => {
      const current = prev[roleId] || [];
      const updated = current.includes(menuId)
        ? current.filter((p) => p !== menuId)
        : [...current, menuId];
      return { ...prev, [roleId]: updated };
    });
    setHasChanges(true);
  };

  // Save all changes. Each role is saved on its own request, so some may be saved while
  // another is refused: every request is let finish, then the grid is read again so it
  // shows what was really saved rather than the ticks as clicked.
  const handleSave = async () => {
    setSaving(true);
    try {
      const results = await Promise.allSettled(
        roles.map((role) =>
          menusAPI.updateRoleMenus(role.id, permissionChanges[role.id] || [])
        )
      );
      const refused = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (refused.length === 0) {
        alert('Permissions saved successfully!');
      } else {
        console.error('Error saving permissions:', refused.map((r) => r.reason));
        // The server's own words when it has them: ticking a page that also carries a right
        // (the grey "also:" line) is refused for someone who is not an admin of the project.
        const reason = refused[0].reason as { response?: { data?: { message?: unknown } } } | undefined;
        const message = reason?.response?.data?.message;
        alert(typeof message === 'string' && message ? message : 'Failed to save permissions');
      }
      setHasChanges(false);
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  // Get permission count for a role
  const getPermissionCount = (roleId: string): number => {
    return permissionChanges[roleId]?.length || 0;
  };

  // Delete role
  const handleDeleteRole = async (roleId: string) => {
    setDeletingRole(true);
    try {
      await rolesAPI.delete(roleId);
      setShowDeleteConfirm(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting role:', err);
      alert('Failed to delete role');
    } finally {
      setDeletingRole(false);
    }
  };

  // Edit role
  const openEditRole = (role: Role) => {
    setEditingRole({
      id: role.id,
      name: role.name,
      description: role.description || '',
      geoBased: role.geo_based_attendance || false,
      awsVerification: role.aws_face_verification || false,
      uniformCheckEngine: uniformCheckEngineOf(role.uniform_check_engine),
      multiStore: role.multi_store_deployment || false,
      allProject: role.data_scope === 'all_project',
    });
  };

  const handleEditRole = async () => {
    if (!editingRole) return;
    setSavingEdit(true);
    try {
      await rolesAPI.update(editingRole.id, {
        name: editingRole.name,
        description: editingRole.description,
        geo_based_attendance: editingRole.geoBased,
        aws_face_verification: editingRole.awsVerification,
        // Hidden while the toggle is off, and then left as saved.
        ...(editingRole.awsVerification ? { uniform_check_engine: editingRole.uniformCheckEngine } : {}),
        multi_store_deployment: editingRole.multiStore,
        data_scope: editingRole.allProject ? 'all_project' : 'hierarchy',
      });
      setEditingRole(null);
      fetchData();
    } catch (err: any) {
      console.error('Error updating role:', err);
      alert(err.response?.data?.message || 'Failed to update role');
    } finally {
      setSavingEdit(false);
    }
  };

  // Get role name by ID
  const getRoleName = (roleId: string): string => {
    return roles.find((r) => r.id === roleId)?.name || 'this role';
  };

  // Check if role can be deleted (no users assigned)
  const canDeleteRole = (role: Role): boolean => {
    return (role.user_count || 0) === 0;
  };

  useImperativeHandle(ref, () => ({
    onSave: handleSave,
    saving,
    hasChanges,
  }));

  // Notify parent when saving/hasChanges state changes so Save button re-renders
  useEffect(() => {
    props.onChangeNotify?.();
  }, [hasChanges, saving]);

  // Group menus by parent into the shared FeatureMatrixTable shape. A parent
  // with no children renders as a singleton (one flat row labelled with the
  // parent name); a parent with children renders as a header + indented child
  // rows. Mirrors the MobileAccessTab grouping. Under a page whose tick also carries a
  // right, a grey line says what (pageRightNotes).
  const notes = pageRightNotes(menuItems, pageRights);
  const menuGroups: FeatureMatrixGroup[] = menuItems
    .filter((m) => !m.parent_id)
    .sort((a, b) => a.display_order - b.display_order)
    .map((parent) => {
      const children = menuItems
        .filter((m) => m.parent_id === parent.id)
        .sort((a, b) => a.display_order - b.display_order);
      return {
        key: parent.id,
        label: parent.name,
        note: notes.group.get(parent.id),
        rows: children.length === 0
          ? [{ key: parent.id, label: parent.name, note: notes.row.get(parent.id) }]
          : children.map((child) => ({ key: child.id, label: child.name, note: notes.row.get(child.id) })),
      };
    });

  if (!selectedProjectId) {
    return (
      <NoProjectSelectedState message="Select a project from the dropdown in the menu bar to manage roles." />
    );
  }

  if (loading) {
    return <CenteredLoader />;
  }

  if (error) {
    return <RetryErrorState error={error} onRetry={fetchData} />;
  }

  return (
    <div className="space-y-6">

      {/* Info banner when no roles exist */}
      {roles.length === 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          No roles found for this project. Create a role using the "Add Role" button above to assign menu access.
        </div>
      )}

      {/* Matrix Table */}
      <FeatureMatrixTable
        headerLabel="Menus/Roles"
        roles={roles}
        groups={menuGroups}
        isChecked={(roleId, menuId) => hasPermission(roleId, menuId)}
        onToggle={(roleId, menuId) => togglePermission(roleId, menuId)}
        onEditRole={(role) => openEditRole(role)}
        countLabel={(role) => <>{getPermissionCount(role.id)} access</>}
        roleHeaderActionRowGapClassName="gap-2"
        roleHeaderExtraActions={(role) =>
          canDeleteRole(role) && (
            <button
              onClick={() => setShowDeleteConfirm(role.id)}
              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Delete role"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )
        }
      />


      {/* Delete Confirmation Modal */}
      {/* Edit Role Modal */}
      {editingRole && (
        <EditRoleModal
          key={editingRole.id}
          value={editingRole}
          onChange={setEditingRole}
          onClose={() => setEditingRole(null)}
          onSave={handleEditRole}
          saving={savingEdit}
          projectId={selectedProjectId}
          extraToggles={
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Project-wide data access</label>
                <p className="text-xs text-gray-500">On: sees all employees in the project (reports). Off: only their reporting hierarchy.</p>
              </div>
              <button
                type="button"
                onClick={() => setEditingRole({ ...editingRole, allProject: !editingRole.allProject })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editingRole.allProject ? 'bg-primary-600' : 'bg-gray-200'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editingRole.allProject ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          }
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Role</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">{getRoleName(showDeleteConfirm)}</span>? This action cannot be undone.
            </p>
            <ConfirmDeleteActions
              onCancel={() => setShowDeleteConfirm(null)}
              onConfirm={() => handleDeleteRole(showDeleteConfirm)}
              loading={deletingRole}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default WebAccessPage;
